import {
  isLegalRaise,
  legalRaises,
  minLegalRaise,
} from '../../shared/rules';
import { FACES, type BidValue, type ClientView, type Face } from '../../shared/types';
import { die, el } from '../dom';

export type BidActions = {
  onBid: (bid: BidValue) => void;
  onChallenge: () => void;
  onExact: () => void;
};

/** Dice on the table right now — the ceiling on any bid quantity (§3.2). */
function totalDice(view: ClientView): number {
  return view.players.reduce((sum, p) => sum + p.diceCount, 0);
}

/**
 * Quantity stepper + face picker.
 *
 * Illegal combinations are visibly disabled and the default is the minimum legal
 * raise, per §12. This is a convenience only: §8 revalidates everything server
 * side, and the same `isLegalRaise` runs in both places so they cannot disagree.
 */
export function renderBidding(
  view: ClientView,
  draft: BidValue | null,
  setDraft: (bid: BidValue) => void,
  actions: BidActions,
): HTMLElement {
  const ctx = { totalDice: totalDice(view), palifico: false };
  const current = view.currentBid;
  const yourTurn = view.activePlayerId === view.you.id;

  const candidates = legalRaises(current, ctx);
  const minimum = minLegalRaise(current, ctx);
  const selected = draft ?? minimum;

  const panel = el('section', {
    class: 'bidding',
    'aria-label': 'Place your bid',
  });

  if (!yourTurn) {
    panel.appendChild(
      el('p', {
        class: 'bidding__waiting',
        text:
          current === null
            ? 'Waiting for the opening bid…'
            : 'Waiting for the other players…',
      }),
    );
    return panel;
  }

  if (selected === null) {
    panel.appendChild(
      el('p', {
        class: 'bidding__waiting',
        text: 'No raise is possible — you must challenge.',
      }),
    );
    panel.appendChild(challengeButton(view, actions));
    return panel;
  }

  // --- quantity -----------------------------------------------------------
  const quantities = [...new Set(candidates.map((c) => c.quantity))].sort(
    (a, b) => a - b,
  );
  const lowest = quantities[0] ?? selected.quantity;
  const highest = quantities[quantities.length - 1] ?? selected.quantity;

  const step = (delta: number): void => {
    const next = selected.quantity + delta;
    // Snap to a quantity that has at least one legal face.
    const target = quantities.includes(next)
      ? next
      : delta > 0
        ? quantities.find((q) => q > selected.quantity)
        : [...quantities].reverse().find((q) => q < selected.quantity);
    if (target === undefined) return;

    const face = firstLegalFace(current, target, ctx, selected.face);
    if (face !== null) setDraft({ quantity: target, face });
  };

  const decrement = el('button', {
    type: 'button',
    // Stable ids let `swap` in main.ts put keyboard focus back after the
    // re-render that every action triggers. Without them, pressing + once
    // drops focus to <body> and a keyboard player has to Tab back each time.
    id: 'qty-minus',
    class: 'stepper__button',
    'aria-label': 'Fewer dice',
    disabled: selected.quantity <= lowest,
  }, ['−']);
  decrement.addEventListener('click', () => step(-1));

  const increment = el('button', {
    type: 'button',
    id: 'qty-plus',
    class: 'stepper__button',
    'aria-label': 'More dice',
    disabled: selected.quantity >= highest,
  }, ['+']);
  increment.addEventListener('click', () => step(1));

  panel.appendChild(
    el('div', { class: 'field' }, [
      el('span', { class: 'field__label', id: 'qty-label', text: 'How many' }),
      el('div', { class: 'stepper', role: 'group', 'aria-labelledby': 'qty-label' }, [
        decrement,
        el('output', {
          class: 'stepper__value',
          'aria-live': 'polite',
          text: String(selected.quantity),
        }),
        increment,
      ]),
    ]),
  );

  // --- face ---------------------------------------------------------------
  const faceRow = el('div', {
    class: 'faces',
    role: 'radiogroup',
    'aria-labelledby': 'face-label',
  });

  for (const face of FACES) {
    const legal = isLegalRaise(current, { quantity: selected.quantity, face }, ctx);
    const chosen = face === selected.face;

    const button = el('button', {
      type: 'button',
      id: `face-${face}`,
      class: `face ${chosen ? 'face--on' : ''}`,
      role: 'radio',
      'aria-checked': chosen ? 'true' : 'false',
      'aria-label': face === 1 ? 'Ones (wild)' : `${face}s`,
      disabled: !legal,
      // Explains *why* a face is unavailable rather than just greying it out.
      title: legal
        ? undefined
        : face === 1
          ? 'Ones are wild — they need a different quantity'
          : 'Not a legal raise at this quantity',
    }, [die(face, 'plain')]);

    button.addEventListener('click', () =>
      setDraft({ quantity: selected.quantity, face }),
    );
    faceRow.appendChild(button);
  }

  panel.appendChild(
    el('div', { class: 'field' }, [
      el('span', { class: 'field__label', id: 'face-label', text: 'Showing' }),
      faceRow,
    ]),
  );

  // --- actions ------------------------------------------------------------
  const bidLabel =
    selected.face === 1
      ? `Bid ${selected.quantity} × ones (wild)`
      : `Bid ${selected.quantity} × ${selected.face}s`;

  const bid = el('button', {
    type: 'button',
    id: 'bid-submit',
    class: 'button button--primary',
    disabled: !isLegalRaise(current, selected, ctx),
    text: bidLabel,
  });
  bid.addEventListener('click', () => actions.onBid(selected));

  const row = el('div', { class: 'bidding__actions' }, [bid]);
  if (current !== null) row.appendChild(challengeButton(view, actions));
  if (view.config.allowExact && current !== null) {
    const exact = el('button', {
      type: 'button',
      class: 'button',
      text: 'Exact',
    });
    exact.addEventListener('click', actions.onExact);
    row.appendChild(exact);
  }

  panel.appendChild(row);
  return panel;
}

function challengeButton(view: ClientView, actions: BidActions): HTMLElement {
  const bid = view.currentBid;
  const button = el('button', {
    type: 'button',
    id: 'challenge',
    class: 'button button--danger',
    disabled: bid === null,
    text: bid === null ? 'Challenge' : `Challenge ${bid.quantity} × ${bid.face}s`,
  });
  button.addEventListener('click', actions.onChallenge);
  return button;
}

/** Prefer keeping the current face when stepping quantity; else the lowest legal. */
function firstLegalFace(
  current: BidValue | null,
  quantity: number,
  ctx: { totalDice: number; palifico: boolean },
  preferred: Face,
): Face | null {
  if (isLegalRaise(current, { quantity, face: preferred }, ctx)) return preferred;
  for (const face of FACES) {
    if (isLegalRaise(current, { quantity, face }, ctx)) return face;
  }
  return null;
}
